import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Collapse,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  DashboardOutlined,
  AccountBalanceOutlined,
  ReceiptOutlined,
  SwapHorizOutlined,
  FolderOutlined,
  HomeWorkOutlined,
  ApartmentOutlined,
  DescriptionOutlined,
  AssessmentOutlined,
  ExpandLess,
  ExpandMore,
  TrendingUpOutlined,
  AccountBalanceWalletOutlined,
  BusinessCenterOutlined,
  SecurityOutlined,
  CalculateOutlined,
  AddCircleOutlineOutlined,
  AccountBalance,
  ViewKanbanOutlined,
  BoltOutlined,
  PaymentsOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { tritonStyles } from '../styles/TritonTheme';

// Styled Components
const TritonDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: tritonStyles.gradients.light,
    borderRight: '1px solid #e2e8f0',
    boxShadow: tritonStyles.elevations.medium,
    overflow: 'auto',
  },
}));

const NavigationHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2, 2, 2),
  background: tritonStyles.gradients.primary,
  color: '#ffffff',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const NavigationList = styled(List)(({ theme }) => ({
  padding: theme.spacing(2, 1),
  '& .MuiListItem-root': {
    marginBottom: theme.spacing(0.5),
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0, 0.5),
  padding: theme.spacing(1.5, 2),
  transition: tritonStyles.transitions.standard,
  position: 'relative',
  
  // Standard-Zustand
  '&:hover': {
    backgroundColor: 'rgba(26, 54, 93, 0.06)',
    transform: 'translateX(4px)',
    '& .MuiListItemIcon-root': {
      color: '#1a365d',
    },
    '& .MuiListItemText-primary': {
      color: '#1a365d',
      fontWeight: 500,
    },
  },
  
  // Aktiver Zustand
  ...(active && {
    backgroundColor: 'rgba(26, 54, 93, 0.1)',
    borderLeft: `4px solid #1a365d`,
    marginLeft: 0,
    paddingLeft: theme.spacing(1.5),
    
    '& .MuiListItemIcon-root': {
      color: '#1a365d',
    },
    '& .MuiListItemText-primary': {
      color: '#1a365d',
      fontWeight: 600,
    },
    
    '&::after': {
      content: '""',
      position: 'absolute',
      right: theme.spacing(2),
      top: '50%',
      transform: 'translateY(-50%)',
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: '#d69e2e',
    },
    
    '&:hover': {
      backgroundColor: 'rgba(26, 54, 93, 0.15)',
      transform: 'translateX(2px)',
    },
  }),
}));

const CategoryDivider = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1, 2),
  '& .MuiTypography-root': {
    color: '#718096',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
}));

const SubMenuItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  paddingLeft: theme.spacing(6),
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0, 0.5),
  transition: tritonStyles.transitions.standard,
  
  '&:hover': {
    backgroundColor: 'rgba(26, 54, 93, 0.04)',
    '& .MuiListItemText-primary': {
      color: '#2d3748',
    },
  },
  
  ...(active && {
    backgroundColor: 'rgba(26, 54, 93, 0.08)',
    '& .MuiListItemText-primary': {
      color: '#1a365d',
      fontWeight: 500,
    },
  }),
}));

// Navigation-Konfiguration
const navigationConfig = [
  {
    category: 'Übersicht',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: DashboardOutlined,
        path: '/dashboard',
        badge: null,
      },
      {
        id: 'todo-management',
        label: 'ToDo Kanban',
        icon: ViewKanbanOutlined,
        path: '/dashboard/todo-management',
      },
    ],
  },
  {
    category: 'Buchhaltung',
    items: [
      {
        id: 'bookings',
        label: 'Buchungen',
        icon: ReceiptOutlined,
        children: [
          { id: 'booking-list', label: 'Buchungsübersicht', path: '/dashboard/buchungen' },
          { id: 'kontenplan', label: 'Kontenplan', path: '/dashboard/kontenplan' },
        ],
      },
      {
        id: 'transactions',
        label: 'Transaktionen',
        icon: SwapHorizOutlined,
        children: [
          { id: 'kontobewegungen', label: 'Kontobewegungen', path: '/dashboard/kontobewegungen' },
          { id: 'csv-upload', label: 'CSV Import', path: '/dashboard/csv-upload' },
        ],
      },
      {
        id: 'cashflow',
        label: 'Cash-Flow Analyse',
        icon: TrendingUpOutlined,
        path: '/dashboard/cashflow',
      },
    ],
  },
  {
    category: 'Immobilien',
    items: [
      {
        id: 'properties',
        label: 'Objekte',
        icon: HomeWorkOutlined,
        children: [
          { id: 'objects-list', label: 'Alle Objekte', path: '/dashboard/objekte' },
          { id: 'objects-add', label: 'Neues Objekt', path: '/dashboard/objekte/neu' },
        ],
      },
      {
        id: 'units',
        label: 'Wohneinheiten',
        icon: ApartmentOutlined,
        path: '/dashboard/wohneinheiten',
      },
      {
        id: 'sales-offers',
        label: 'Verkaufsangebote',
        icon: BusinessCenterOutlined,
        path: '/dashboard/verkaufsangebote',
      },
      {
        id: 'leases',
        label: 'Mietverträge',
        icon: DescriptionOutlined,
        path: '/dashboard/mietvertrag',
      },
      {
        id: 'rent-adjustments',
        label: 'Mietanpassungen',
        icon: TrendingUpOutlined,
        path: '/dashboard/mietanpassungen',
      },
      {
        id: 'rent-payments',
        label: 'Mietzahlungen',
        icon: PaymentsOutlined,
        path: '/dashboard/forderungsmanagement?tab=mietzahlungen',
      },
    ],
  },
  {
    category: 'Forderungsmanagement',
    items: [
      {
        id: 'fm-dashboard',
        label: 'Übersicht',
        icon: AccountBalance,
        path: '/dashboard/forderungsmanagement?tab=dashboard',
      },
      {
        id: 'fm-forderungen',
        label: 'Forderungen',
        icon: DescriptionOutlined,
        path: '/dashboard/forderungsmanagement?tab=forderungen',
      },
      {
        id: 'fm-energie',
        label: 'Energieverträge',
        icon: BoltOutlined,
        path: '/dashboard/forderungsmanagement?tab=energievertraege',
      },
      {
        id: 'fm-vorschlaege',
        label: 'Vorschläge',
        icon: BoltOutlined,
        path: '/dashboard/forderungsmanagement?tab=vorschlaege',
      },
      {
        id: 'fm-versicherungen',
        label: 'Versicherungen',
        icon: SecurityOutlined,
        path: '/dashboard/forderungsmanagement?tab=versicherungen',
      },
      {
        id: 'fm-abgaben',
        label: 'Abgaben',
        icon: AccountBalance,
        path: '/dashboard/forderungsmanagement?tab=abgaben',
      },
      {
        id: 'fm-vorlagen',
        label: 'Vorlagen',
        icon: DescriptionOutlined,
        path: '/dashboard/forderungsmanagement?tab=vorlagen',
      },
    ],
  },
  {
    category: 'Bankwesen',
    items: [
      {
        id: 'bank-overview',
        label: 'Portfolio-Übersicht',
        icon: AccountBalanceOutlined,
        path: '/dashboard/bankwesen',
      },
      {
        id: 'loan-contracts',
        label: 'Darlehensverträge',
        icon: AccountBalanceWalletOutlined,
        path: '/dashboard/darlehensvertraege',
      },
    ],
  },
  {
    category: 'Nebenkosten',
    items: [
      {
        id: 'nebenkosten-overview',
        label: 'Übersicht',
        icon: CalculateOutlined,
        path: '/dashboard/nebenkostenabrechnung',
      },
      {
        id: 'nebenkosten-zaehlerstaende',
        label: 'Zählerstände',
        icon: CalculateOutlined,
        path: '/dashboard/nebenkostenabrechnung/zaehlerstaende',
      },
      {
        id: 'nebenkosten-zaehler-erfassen',
        label: 'Zähler erfassen',
        icon: AddCircleOutlineOutlined,
        path: '/dashboard/nebenkostenabrechnung/zaehler-erfassen',
      },
    ],
  },
  {
    category: 'Steuern',
    items: [
      { id: 'ust-voranmeldung', label: 'USt-Voranmeldung', icon: AssessmentOutlined, path: '/dashboard/ust-voranmeldung' },
      { id: 'nk-ust-prognose', label: 'NK-USt-Prognosen', icon: AssessmentOutlined, path: '/dashboard/nk-ust-prognose' },
      { id: 'anlage-vv', label: 'Anlage V+V', icon: AssessmentOutlined, path: '/dashboard/anlage-vv' },
      { id: 'afa-verwaltung', label: 'AfA-Verwaltung', icon: AssessmentOutlined, path: '/dashboard/steuern/afa-verwaltung' },
    ],
  },
  {
    category: 'Dokumente',
    items: [
      {
        id: 'documents',
        label: 'Dokumentenarchiv',
        icon: FolderOutlined,
        children: [
          { id: 'documents-eingang', label: 'Dokumenteneingang', path: '/dashboard/dokumenten-eingang' },
          { id: 'documents-energy-inbox', label: 'Energie-Dokument-Inbox', path: '/dashboard/energie-dokument-inbox' },
        ],
      },
      {
        id: 'receipts',
        label: 'Beleg-Management',
        icon: ReceiptOutlined,
        path: '/dashboard/belege',
      },
    ],
  },
  {
    category: 'Verwaltung',
    items: [
      {
        id: 'users',
        label: 'Benutzerverwaltung',
        icon: SecurityOutlined,
        path: '/dashboard/benutzer',
      },
    ],
  },
];

const TritonSidebar = ({ open, onClose, currentPath = '/dashboard', variant = 'permanent' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedItems, setExpandedItems] = useState({});

  const handleExpandClick = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      if (isMobile) onClose();
    }
  };

  const isActivePath = (path) => {
    if (!path) return false;
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  const isActiveParent = (children) => {
    if (!children) return false;
    return children.some(child => isActivePath(child.path));
  };

  const renderNavigationItem = (item, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.id];
    const isActive = isActivePath(item.path) || isActiveParent(item.children);

    if (hasChildren) {
      return (
        <React.Fragment key={item.id}>
          <StyledListItemButton
            active={isActive}
            onClick={() => handleExpandClick(item.id)}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#4a5568' }}>
              <item.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#1a365d' : '#2d3748',
              }}
            />
            {item.badge && (
              <Badge
                badgeContent={item.badge}
                color="warning"
                sx={{
                  mr: 1,
                  '& .MuiBadge-badge': {
                    backgroundColor: '#d69e2e',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                  }
                }}
              />
            )}
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </StyledListItemButton>
          
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => (
                <SubMenuItem
                  key={child.id}
                  active={isActivePath(child.path)}
                  onClick={() => handleNavigation(child.path)}
                >
                  <ListItemText
                    primary={child.label}
                    primaryTypographyProps={{
                      fontSize: '0.8125rem',
                      color: isActivePath(child.path) ? '#1a365d' : '#4a5568',
                    }}
                  />
                </SubMenuItem>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <StyledListItemButton
        key={item.id}
        active={isActive}
        onClick={() => handleNavigation(item.path)}
      >
        <ListItemIcon sx={{ minWidth: 40, color: '#4a5568' }}>
          <item.icon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? '#1a365d' : '#2d3748',
          }}
        />
        {item.badge && (
          <Badge
            badgeContent={item.badge}
            color="warning"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#d69e2e',
                color: '#ffffff',
                fontSize: '0.75rem',
              }
            }}
          />
        )}
      </StyledListItemButton>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation Header */}
      <NavigationHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BusinessCenterOutlined sx={{ fontSize: 24, mr: 1.5, color: '#d69e2e' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            Navigation
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.8125rem' }}>
          Professionelle Immobilienverwaltung
        </Typography>
      </NavigationHeader>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflowY: 'auto', pb: 2 }}>
        {navigationConfig.map((category, categoryIndex) => (
          <React.Fragment key={category.category}>
            <CategoryDivider>
              <Typography variant="overline">
                {category.category}
              </Typography>
            </CategoryDivider>
            
            <NavigationList disablePadding>
              {category.items.map(item => renderNavigationItem(item))}
            </NavigationList>
            
            {categoryIndex < navigationConfig.length - 1 && (
              <Divider sx={{ mx: 2, my: 1, borderColor: '#e2e8f0' }} />
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', backgroundColor: '#f7fafc' }}>
        <Typography variant="caption" sx={{ color: '#718096', textAlign: 'center', display: 'block' }}>
          Version 2.1.0 - immo-automation GmbH
        </Typography>
      </Box>
    </Box>
  );

  return (
    <TritonDrawer
      variant={variant}
      open={variant === 'permanent' ? true : open}
      onClose={onClose}
      ModalProps={variant === 'temporary' ? { keepMounted: true } : undefined}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          ...(variant === 'permanent' && {
            position: 'fixed',
            top: 80, // unterhalb Header
            height: 'calc(100vh - 80px)',
          }),
        },
      }}
    >
      {drawerContent}
    </TritonDrawer>
  );
};

export default TritonSidebar;
